import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { usePostComments, type CommunityPost, type CommunityComment } from "@/hooks/useCommunity";
import { useAuth } from "@/lib/auth";

interface PostDetailModalProps {
  post: CommunityPost | null;
  onToggleLike?: (postId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const categoryLabel: Record<string, string> = {
  "공지/이벤트": "공지/이벤트",
  "자유수다": "자유수다",
  "물건 Q&A": "물건 Q&A",
  "낙찰/임장 후기": "낙찰/임장 후기",
};

const categoryStyle: Record<string, string> = {
  "공지/이벤트": "bg-red-50 text-red-600 border-red-200",
  "자유수다": "bg-secondary-50 text-secondary-700 border-secondary-200",
  "물건 Q&A": "bg-accent-50 text-accent-700 border-accent-200",
  "낙찰/임장 후기": "bg-emerald-50 text-emerald-600 border-emerald-200",
};

const badgeStyle = (badge?: string) => {
  if (badge === "마스터") return "bg-amber-50 text-amber-700 border-amber-200";
  if (badge === "정수강생") return "bg-emerald-50 text-emerald-600 border-emerald-200";
  return "";
};

function CommentItem({ comment }: { comment: CommunityComment & { isAI?: boolean } }) {
  return (
    <div className={`flex gap-3 ${comment.isAI ? "bg-accent-50/50 rounded-xl p-4 border border-accent-200" : "py-3"}`}>
      {comment.isAI ? (
        <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center shrink-0">
          <i className="ri-robot-2-line text-sm text-accent-700"></i>
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-foreground-600">{comment.author[0]}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-foreground-950">{comment.author}</span>
          {comment.authorBadge && (
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeStyle(comment.authorBadge)}`}>
              {comment.authorBadge}
            </span>
          )}
          {comment.isAI && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full border bg-accent-100 text-accent-700 border-accent-200">
              AI 답변
            </span>
          )}
          <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">{comment.createdAt}</span>
        </div>
        <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">{comment.content}</p>
      </div>
    </div>
  );
}

export default function PostDetailModal({ post, isOpen, onClose, onToggleLike }: PostDetailModalProps) {
  const { user } = useAuth();
  const { comments, addComment } = usePostComments(post?.id ?? null);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  if (!post) return null;

  const isQnA = post.category === "물건 Q&A";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
          ></motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative bg-white rounded-2xl max-w-[640px] w-full max-h-[85vh] overflow-y-auto"
          >
            <div className="p-6 md:p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${categoryStyle[post.category]}`}>
                    {categoryLabel[post.category]}
                  </span>
                  {post.resolved && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200">
                      해결됨
                    </span>
                  )}
                  {post.pinned && (
                    <span className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-500 rounded-full border border-red-200">
                      <i className="ri-pushpin-fill text-xs mr-0.5"></i>고정
                    </span>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Title */}
              <h2 className="text-lg font-bold text-foreground-950 mb-3 leading-snug">{post.title}</h2>

              {/* Meta */}
              <div className="flex items-center gap-2 flex-wrap mb-5 pb-5 border-b border-foreground-200">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center">
                    <span className="text-[11px] font-bold text-foreground-600">{post.author[0]}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground-950">{post.author}</span>
                  {post.authorBadge && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${badgeStyle(post.authorBadge)}`}>
                      {post.authorBadge}
                    </span>
                  )}
                </div>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{post.createdAt}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">조회 {post.views.toLocaleString()}</span>
              </div>

              {/* Content */}
              <div className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line mb-6">{post.content}</div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex items-center gap-1.5 mb-6">
                  {post.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-background-100 text-foreground-500">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Likes & Comments Info */}
              <div className="flex items-center gap-4 mb-5 pb-5 border-b border-foreground-200">
                <button
                  onClick={() => post && onToggleLike?.(post.id)}
                  disabled={!user}
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-red-400 transition-colors disabled:opacity-50"
                  title={user ? "" : "로그인 후 이용"}
                >
                  <i className="ri-heart-line text-lg"></i>
                  <span>{post.likes}</span>
                </button>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-chat-3-line text-lg"></i>
                  <span>{post.commentCount}</span>
                </div>
              </div>

              {/* AI 정답 안내 for Q&A */}
              {isQnA && (
                <div className="mb-5 p-4 rounded-xl bg-accent-50 border border-accent-200 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-200 flex items-center justify-center shrink-0">
                    <i className="ri-robot-2-line text-base text-accent-700"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-accent-800 mb-0.5">AI 경매비서 안내</p>
                    <p className="text-xs text-accent-700 leading-relaxed">
                      처음 질문이 등록되면 AI 경매비서가 1분 내로 1차 권리분석 답변을 달아드립니다. 정확한 분석을 위해 구체적인 사건번호나 등기부 정보를 함께 공유해주시면 더 빠르고 정확한 답변이 가능합니다.
                    </p>
                  </div>
                </div>
              )}

              {/* Comments */}
              <div>
                <h3 className="text-sm font-bold text-foreground-950 mb-3">댓글 {comments.length}개</h3>
                <div className="space-y-1 mb-4">
                  {comments.map((comment) => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
                {user ? (
                  <div className="flex gap-2">
                    <input
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="댓글을 입력하세요"
                      className="flex-1 px-3 py-2 text-sm border border-foreground-200 rounded-lg focus:outline-none focus:border-[#C4956A]"
                    />
                    <button
                      disabled={posting || !commentText.trim()}
                      onClick={async () => {
                        setPosting(true);
                        const res = await addComment(commentText.trim());
                        setPosting(false);
                        if (res.ok) setCommentText("");
                      }}
                      className="px-4 py-2 text-sm rounded-lg bg-foreground-950 text-white disabled:opacity-50 whitespace-nowrap"
                    >
                      등록
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 py-2">댓글 작성은 로그인 후 이용할 수 있습니다.</p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}